import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CreditCard, Gift, Sparkles, ShieldCheck, Copy, Calendar, AlertTriangle } from "lucide-react";

const STATUS_COLOR = {
  trial: { bg: "#EFF6FF", fg: "#1E3A8A", label: "Trial" },
  active: { bg: "#EFF6FF", fg: "#1E3A8A", label: "Activo" },
  grace: { bg: "#FFFBEB", fg: "#92400E", label: "Periodo de gracia" },
  expired: { bg: "#FEF2F2", fg: "#991B1B", label: "Vencido" },
};

export default function AdminBilling() {
  const [sub, setSub] = useState(null);
  const [keyInfo, setKeyInfo] = useState({ has_key: false, masked: null });
  const [stripeInput, setStripeInput] = useState("");
  const [referrals, setReferrals] = useState({ code: null, total_uses: 0, free_months_granted: 0, uses: [] });
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, k, r] = await Promise.all([
        api.get("/platform/subscription/status"),
        api.get("/platform/stripe-key"),
        api.get("/platform/referrals"),
      ]);
      setSub(s.data); setKeyInfo(k.data); setReferrals(r.data);
    } catch (err) {
      console.error("Billing load failed:", err?.message || err);
      toast.error("No se pudieron cargar los datos de billing");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Auto-confirm if returning from Stripe checkout
  useEffect(() => {
    const url = new URL(window.location.href);
    const sid = url.searchParams.get("session_id");
    if (sid) {
      setSessionId(sid);
      (async () => {
        try {
          await api.get(`/platform/subscription/status-poll/${sid}`);
        } catch (err) {
          console.warn("Poll failed:", err);
        }
        load();
      })();
    }
  }, [load]);

  async function saveStripeKey() {
    setBusy(true);
    try {
      await api.put("/platform/stripe-key", { stripe_secret_key: stripeInput });
      toast.success("Stripe key guardada");
      setStripeInput("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    } finally { setBusy(false); }
  }

  async function payNow() {
    setBusy(true);
    try {
      const { data } = await api.post("/platform/subscription/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error iniciando pago");
      setBusy(false);
    }
  }

  async function manualConfirm() {
    if (!sessionId) return;
    setBusy(true);
    try {
      await api.post(`/platform/subscription/confirm/${sessionId}`);
      toast.success("Pago confirmado · Suscripción extendida");
      window.history.replaceState({}, "", "/admin/billing");
      setSessionId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se encontró el pago");
    } finally { setBusy(false); }
  }

  function copyCode() {
    if (referrals.code) {
      navigator.clipboard.writeText(referrals.code);
      toast.success("Copiado");
    }
  }

  const statusInfo = STATUS_COLOR[sub?.status || "trial"] || STATUS_COLOR.trial;

  return (
    <div>
      <div className="mb-8">
        <p className="overline">Suscripción</p>
        <h1 className="font-display text-4xl font-light tracking-tight mt-3">Billing</h1>
      </div>

      {/* Subscription card */}
      <Card className="border-slate-200 mb-6" data-testid="billing-subscription-card">
        <CardContent className="p-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-[hsl(var(--accent))]" />
                <p className="overline">Estado de la cuenta</p>
                <Badge style={{ backgroundColor: statusInfo.bg, color: statusInfo.fg }} data-testid="billing-status-badge">{statusInfo.label}</Badge>
              </div>
              <p className="font-display text-3xl font-light mt-3">
                {sub?.status === "trial" && `${sub.days_remaining?.toFixed(0)} días de trial`}
                {sub?.status === "active" && `Vence en ${sub.days_remaining?.toFixed(0)} días`}
                {sub?.status === "grace" && `${sub.days_remaining?.toFixed(0)} días de gracia`}
                {sub?.status === "expired" && "Cuenta vencida"}
              </p>
              {sub?.valid_until && <p className="text-xs text-slate-500 mt-2">Vigente hasta: {new Date(sub.valid_until).toLocaleString("es-MX")}</p>}
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500">Próximo cobro</p>
              <p className="font-display text-4xl font-light">${sub?.amount_due_next?.toFixed(2)} <span className="text-base text-slate-500">MXN</span></p>
              <p className="text-xs text-slate-500 mt-1">
                ${sub?.plan_base_price?.toFixed(0)} base + ${sub?.plan_per_staff_price?.toFixed(0)} × {sub?.active_staff} profesional(es)
                {sub?.first_month_discount > 0 && !sub?.first_month_consumed && (
                  <span className="ml-1 text-[hsl(var(--accent))] font-semibold"> (50% off primer mes aplicado)</span>
                )}
              </p>
              {sub?.is_founder && (
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-xs font-semibold text-amber-800" data-testid="billing-founder-badge">
                  ⭐ Plan Fundador · Sin cobro de por vida
                </div>
              )}
              {sub?.courtesy_until && !sub?.is_founder && new Date(sub.courtesy_until) > new Date() && (
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-sky-50 border border-sky-200 px-3 py-1 text-xs font-semibold text-sky-800" data-testid="billing-courtesy-badge">
                  🎁 Cortesía activa hasta {sub.courtesy_until.slice(0, 10)}
                </div>
              )}
              {sub?.amount_due_breakdown?.has_iva && (
                <div className="mt-3 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-left inline-block" data-testid="billing-iva-breakdown">
                  <div className="flex justify-between gap-6"><span className="text-slate-500">Subtotal</span><span>${sub.amount_due_breakdown.subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between gap-6"><span className="text-slate-500">IVA (16%)</span><span>${sub.amount_due_breakdown.iva.toFixed(2)}</span></div>
                  <div className="flex justify-between gap-6 border-t border-slate-200 pt-1 mt-1 font-semibold"><span>Total</span><span>${sub.amount_due_breakdown.total.toFixed(2)}</span></div>
                </div>
              )}
              <div className="mt-3">
                <Button onClick={payNow} disabled={busy} data-testid="billing-pay-btn">
                  <CreditCard className="h-4 w-4 mr-2" /> Pagar ahora
                </Button>
              </div>
            </div>
          </div>
          {sessionId && (
            <div className="mt-4 p-3 rounded-md bg-secondary border border-[hsl(var(--accent))]/40 flex items-center justify-between" data-testid="billing-pending-confirm">
              <div className="text-sm">
                <p className="font-medium" style={{ color: "#1E3A8A" }}>¿Ya completaste el pago?</p>
                <p className="text-xs text-slate-600">Si Stripe ya cobró tu tarjeta, confirma para extender tu suscripción.</p>
              </div>
              <Button size="sm" variant="outline" onClick={manualConfirm} disabled={busy} data-testid="billing-confirm-payment-btn">
                Confirmar pago
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stripe key */}
      <Card className="border-slate-200 mb-6" data-testid="billing-stripe-key-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-3">
            <ShieldCheck className="h-5 w-5 text-[hsl(var(--accent))]" />
            <p className="overline">Tu Stripe (para cobrar a tus clientes)</p>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            Cuando configures tu key, los <strong>depósitos de citas</strong> irán directo a tu cuenta Stripe.
            Sin esto, el pre-pago no funciona en tu URL pública.
          </p>
          {keyInfo.has_key ? (
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm flex items-center justify-between" data-testid="stripe-key-set">
              <div>
                <p className="text-emerald-800 font-medium">Stripe configurado</p>
                <p className="text-xs text-emerald-700 font-mono mt-1">{keyInfo.masked}</p>
              </div>
            </div>
          ) : (
            <p className="text-xs text-amber-700 mb-3" data-testid="stripe-key-missing">⚠ Aún no has configurado tu Stripe Secret Key.</p>
          )}
          <div className="mt-4 space-y-2">
            <Label className="text-xs">{keyInfo.has_key ? "Cambiar key (pega tu sk_test_ o sk_live_)" : "Pega tu Secret Key de Stripe"}</Label>
            <div className="flex gap-2">
              <Input type="password" placeholder="sk_test_..." value={stripeInput} onChange={(e) => setStripeInput(e.target.value)} data-testid="stripe-key-input" />
              <Button onClick={saveStripeKey} disabled={busy || !stripeInput.trim()} data-testid="stripe-key-save-btn">Guardar</Button>
            </div>
            <p className="text-xs text-slate-500">Encuéntrala en <a className="underline" href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer">dashboard.stripe.com/apikeys</a></p>
          </div>
        </CardContent>
      </Card>

      {/* Referrals */}
      <Card className="border-slate-200" data-testid="billing-referrals-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Gift className="h-5 w-5 text-[hsl(var(--accent))]" />
            <p className="overline">Tu código de referido</p>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            Comparte tu código. Por cada negocio que se registre con él, <strong>tú recibes 1 mes gratis</strong>
            y ellos reciben <strong>50% de descuento</strong> en su primer mes.
          </p>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-white p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Tu código</p>
                <p className="font-mono text-2xl mt-1 font-semibold" data-testid="referral-code">{referrals.code || "—"}</p>
              </div>
              <Button variant="outline" size="sm" onClick={copyCode} disabled={!referrals.code} data-testid="referral-copy-btn">
                <Copy className="h-3 w-3 mr-1" /> Copiar
              </Button>
            </div>
            <div className="rounded-lg p-4 text-white" style={{ backgroundColor: "#1E3A8A" }}>
              <p className="text-[10px] uppercase tracking-wider text-slate-300">Meses gratis ganados</p>
              <p className="font-display text-4xl font-light mt-2"><Sparkles className="inline h-5 w-5 mr-1 text-[hsl(var(--accent))]" />{referrals.free_months_granted}</p>
            </div>
          </div>

          {referrals.uses.length > 0 && (
            <div className="mt-6">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Historial</p>
              <div className="space-y-2">
                {referrals.uses.map((u) => (
                  <div key={u.id} className="flex items-center justify-between text-sm border border-slate-200 rounded-md px-3 py-2 bg-slate-50">
                    <span className="text-slate-700">Referido el {new Date(u.created_at).toLocaleDateString("es-MX")}</span>
                    <span className="text-xs text-[hsl(var(--accent))] font-semibold">+1 mes</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
